import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {Box, Card, DialogActions, DialogContent, DialogContentText, DialogTitle, Skeleton, TextField, Typography} from "@mui/material";
import LazyImage from "../../../../../../components/app-specific/lazy-image";
import {useBizFileUrlsResolver} from "../../../../../../hooks/use-biz-file-urls-resolver";
import {ReactComponent as ImagePlaceholder} from "../../../../../../../assets/images/dropzone/image-placeholder.svg";
import DateUtils from "../../../../../../../core/services/date-utils";
import {setReduxDialogState} from "../../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../../core/models/static/routes/redux-store-routes";
import {useDispatch} from "react-redux";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import IconButton from "../../../../../../components/base/button/icon_button";
import {EditOutlined, OpenInNew} from "@mui/icons-material";
import Dialog from "../../../../../../components/base/dialog";
import {Switch} from "../../../../../../components/base/switch";
import Api from "../../../../../../../core/services/api/api";
import AddVendorModal from "../../../../../../components/dialogs/add-vendor";
import Button from "../../../../../../components/base/button/button";
import Utils from "../../../../../../../core/services/utils/utils";
import {usePartnerInformation} from "../../../../../../hooks/use-partner-information";
import VendorAutocomplete from "../../../../../../components/text-fields/autocomplete/vendor-autocomplete";


const initialUpsertConnectionDialogState = {
    open: false,
    vendor: null,
    partnerId: null,
    onClose: () => void 0,
    onConnectionUpdated: () => void 0,
}

const initialCreateConnectionDialogState = {
    open: false,
    onClose: () => void 0,
    onConnectionCreated: () => void 0,
}

const dataGridSaveConfig = {
    name: 'PartnershipViewSellersDataGrid',
    version: 1,
}

const dataGridColumnNames = {
    avatar: 'avatar',
    name: 'name',
    connectedOn: 'connectedOn',
    website: 'website',
    relatedVendor: 'relatedVendor',
    publicProducts: 'publicProducts',
    importedProductsCount: 'importedProductsCount',
    showWarehousesOnOperatorSide: 'showWarehousesOnOperatorSide',
    showWarehousesOnClientSide: 'showWarehousesOnClientSide',
}

const PartnershipViewSellers = () => {
    const [_partners, setPartners] = useState([]);
    const [_requests, setRequests] = useState([]);
    const [upsertConnectionDialogState, setUpsertConnectionDialogState] = useState(initialUpsertConnectionDialogState);
    const [createConnectionDialogState, setCreateConnectionDialogState] = useState(initialCreateConnectionDialogState);
    const [loadingPartners, setLoadingPartners] = useState(true);
    const [loadingRequests, setLoadingRequests] = useState(true);
    const [togglingShowOnOperatorSides, setTogglingShowOnOperatorSides] = useState([]);
    const [togglingShowOnClientSides, setTogglingShowOnClientSides] = useState([]);
    const partnerInformation = usePartnerInformation();
    const isMounted = useIsMounted();
    const dispatch = useDispatch();
    const bizFileUrls = useBizFileUrlsResolver((t) => t.bizFile);

    const [partners, requests] = useMemo(() => {
        const getPartnerLogoUrl = (logo) => (partnerInformation?.configuration?.PartnerLogoUrlBaseAddress ?? "").concat(logo);
        const partners = _partners.map(e => ({
            ...e,
            sellerInfo: e.sellerInfo ? {
                ...e.sellerInfo,
                logo: getPartnerLogoUrl(e.sellerInfo.logo),
            } : undefined,
        }))
        const requests = _requests.map(e => ({
            ...e,
            sellerInfo: e.sellerInfo ? {
                ...e.sellerInfo,
                logo: getPartnerLogoUrl(e.sellerInfo.logo),
            } : undefined,
        }))
        return [partners, requests];
    }, [_partners, _requests, partnerInformation?.configuration?.PartnerLogoUrlBaseAddress])


    /**
     * Fetches the list of partners.
     * @type {(function(): Promise<void>)}
     */
    const getPartners = useCallback(async () => {
        setLoadingPartners(true);
        setLoadingRequests(true);
        const response = await Api.partnerConnectionSellersList();
        if (!isMounted())
            return;
        setLoadingPartners(false);
        setLoadingRequests(false);
        if (response?.resultFlag) {
            setPartners(response.data.filter(e => e.hasApproved));
            setRequests(response.data.filter(e => !e.hasApproved))
        }
    }, [isMounted])

    /**
     * As soon as the component is mounted:
     * - Fetches the list of requests.
     */
    useEffect(() => {
        void getPartners();
    }, [getPartners])

    // /**
    //  * Fetches the requests made for the Seller partners of this view.
    //  * @type {(function(): Promise<void>)|*}
    //  */
    // const getPartnerRequests = useCallback(async () => {
    //     setLoadingRequests(true);
    //     const response = {
    //         resultFlag: true,
    //         data: Array.from({length: 10}, (_, i) => ({
    //             id: `10000000-0000-0000-0000-00000000000${i + 1}.png`,
    //             name: `Seller ${i + 1}`,
    //             imageToken: `10000000-0000-0000-0000-00000000000${i + 1}`,
    //             extension: `png`,
    //             publicProducts: 200,
    //             description: `This is the description for the description of the Seller that wants us to be in a way that i am trying to be the actual reason that they become the buy that they are now.`,
    //             requestedDateTime: DateUtils.serverNow().subtract(i, 'days').format(DateUtils.withoutOffsetFormat),
    //             relatedVendorName: `Vendor ${i + 1}`,
    //             relatedVendorCode: `${i + 1}`,
    //         })),
    //     }
    //     if (!isMounted())
    //         return;
    //     setLoadingRequests(false);
    //     if (response?.resultFlag) {
    //         setRequests(response?.data ?? []);
    //     }
    // }, [isMounted])
    //
    // /**
    //  * As soon as the component mounts:
    //  * - fetches the requests made for the Seller partners of this view
    //  */
    // useEffect(() => {
    //     void getPartnerRequests();
    // }, [getPartnerRequests])

    /**
     * Opens the create connection dialog.
     * @type {(function(): void)}
     */
    const openCreateConnectionDialog = useCallback(() => {
        setCreateConnectionDialogState({
            open: true,
            onConnectionCreated: (request) => setRequests(prevState => [...prevState, request]),
            onClose: () => setCreateConnectionDialogState(initialCreateConnectionDialogState),
        })
    }, [])

    /**
     * Opens the create connection dialog.
     * @type {(function(*): void)}
     */
    const openUpdateConnectionDialog = useCallback((partner) => {
        setUpsertConnectionDialogState({
            onConnectionUpdated: (vendor) => {
                setPartners(prevState => prevState.map(e => e.id === partner.id
                    ? {...e, vendor: {id: vendor.id, name: vendor.name}}
                    : e
                ))
            },
            open: true,
            partnerId: partner.id,
            vendor: partner.vendor,
            onClose: () => setUpsertConnectionDialogState(initialUpsertConnectionDialogState),
        })
    }, [])

    /**
     * Opens the upsert connection dialog.
     * @type {(function(*, *))}
     */
    const cancelRequest = useCallback((request, setLoading) => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: null,
            removedEntryTitle: `The Request to "${request.name}"`,
            onClose: async (confirmed) => {
                if (!confirmed)
                    return;
                setLoading?.(true);
                // TODO: call api to cancel a Seller request.
                const response = {
                    resultFlag: true,
                }
                if (!isMounted())
                    return false
                setLoading?.(false);
                if (response?.resultFlag) {
                    setRequests(prevState => prevState.filter(e => e.id !== request.id));
                }
                return response?.resultFlag ?? false
            }
        }));
    }, [dispatch, isMounted])

    /**
     * Toggle the show warehouses on operator side for the provided partner.
     * @type {(function(*, *): void)}
     */
    const toggleShowWarehousesOnOperatorSide = useCallback(async (partner, checked) => {
        setTogglingShowOnOperatorSides(prevState => [...prevState, partner.id]);
        const response = await Api.partnerConnectionUpdateSellerConfig({
            partnerConnectionId: partner.id,
            showWarehouseForOperators: checked,
            showWarehouseForCustomers: partner.showWarehouseForCustomers,
        })
        if (!isMounted())
            return;
        setTogglingShowOnOperatorSides(prevState => prevState.filter(e => e !== partner.id));
        if (response?.resultFlag) {
            setPartners(prevState => prevState.map(e => e.id === partner.id ? {...e, showWarehouseForOperators: checked} : e))
        }
    }, [isMounted])

    /**
     * Toggle the show warehouses on client side for the provided partner.
     * @type {(function(*, *): void)}
     */
    const toggleShowWarehousesOnClientSide = useCallback(async (partner, checked) => {
        setTogglingShowOnClientSides(prevState => [...prevState, partner.id]);
        const response = await Api.partnerConnectionUpdateSellerConfig({
            partnerConnectionId: partner.id,
            showWarehouseForOperators: partner.showWarehouseForOperators,
            showWarehouseForCustomers: checked,
        })
        if (!isMounted())
            return;
        setTogglingShowOnClientSides(prevState => prevState.filter(e => e !== partner.id));
        if (response?.resultFlag) {
            setPartners(prevState => prevState.map(e => e.id === partner.id ? {...e, showWarehouseForCustomers: checked} : e))
        }
    }, [isMounted])

    const columns = useMemo(() =>
        /**@type {DataGridColumn[]} */
        [
            {
                name: dataGridColumnNames.avatar,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.left,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                width: 0,
            },
            {
                title: "Name",
                name: dataGridColumnNames.name,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.left,
                sortable: false,
            },
            {
                title: "Connected On",
                name: dataGridColumnNames.connectedOn,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.dateTime,
                sortable: false,
            },
            {
                title: "Website",
                name: dataGridColumnNames.website,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: {minWidth: 200, size: 200},
            },
            {
                title: "Related Vendor",
                name: dataGridColumnNames.relatedVendor,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: {minWidth: 200, size: 200},
            },
            {
                title: "Imported Products Count",
                name: dataGridColumnNames.importedProductsCount,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: {minWidth: 170, size: 170},
            },
            // {
            //     title: "Public Products",
            //     name: dataGridColumnNames.publicProducts,
            //     alignment: DataGridColumnAlignments.left,
            //     type: DataGridColumnTypes.string,
            //     sortable: false,
            // },
            {
                title: "Show Warehouses on Operator Side",
                name: dataGridColumnNames.showWarehousesOnOperatorSide,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
            },
            {
                title: "Show Warehouses on Client Side",
                name: dataGridColumnNames.showWarehousesOnClientSide,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
            }
        ], [])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Memo version of table row records used in data grid
     */
    const rows = useMemo(() => {
        return partners?.map(partner => ({
            key: partner?.id,
            data: partner,
            cells: {
                [dataGridColumnNames.avatar]:
                    <div className={'operator-image-profile-list'}>
                        {
                            <LazyImage
                                alt={'profile'}
                                noImage={<ImagePlaceholder style={{height: 50, width: 50, borderRadius: 8}}/>}
                                src={bizFileUrls.getFilePreviewUrl(partner.sellerInfo?.logo)}
                                height={50}
                                style={{height: 50, width: 50, borderRadius: 8}}
                            />
                        }
                    </div>,
                [dataGridColumnNames.name]: partner?.sellerInfo?.name,
                [dataGridColumnNames.connectedOn]: partner?.acceptDateTime,
                [dataGridColumnNames.website]:
                    <>
                        <IconButton
                            className={'mr-2'}
                            color={'secondary'}
                            onPressed={() => window.open(partner.sellerInfo?.website, '_blank')}>
                            <OpenInNew className={'icon small'}/>
                        </IconButton>
                        <a href={partner.website}
                           target={'_blank'}
                           rel="noreferrer"
                           className={'text-sm text-color-1'}
                           {...dataGridCellTextProps}
                        >
                            {Utils.formatLinkUrlDisplay(partner?.sellerInfo?.website)}
                        </a>
                    </>,
                [dataGridColumnNames.relatedVendor]:
                    <>
                        <IconButton
                            className={'mr-2'}
                            color={'secondary'}
                            onPressed={() => openUpdateConnectionDialog(partner)}>
                            <EditOutlined className={'icon small'}/>
                        </IconButton>
                        <p {...dataGridCellTextProps} className={'text-sm'}>
                            {partner?.vendor?.name}
                        </p>
                    </>,
                // [dataGridColumnNames.publicProducts]: partner?.publicProducts,
                [dataGridColumnNames.importedProductsCount]: partner?.importedProductsCount,
                [dataGridColumnNames.showWarehousesOnOperatorSide]:
                    <Switch
                        color={'secondary'}
                        disabled={togglingShowOnOperatorSides.includes(partner.id)}
                        checked={partner.showWarehouseForOperators}
                        onChange={(checked) => toggleShowWarehousesOnOperatorSide(partner, checked)}
                    />,
                [dataGridColumnNames.showWarehousesOnClientSide]:
                    <Switch
                        color={'secondary'}
                        disabled={togglingShowOnClientSides.includes(partner.id)}
                        checked={partner.showWarehouseForCustomers}
                        onChange={(checked) => toggleShowWarehousesOnClientSide(partner, checked)}
                    />,
            }
        })) ?? []
    }, [openUpdateConnectionDialog, partners, toggleShowWarehousesOnClientSide, toggleShowWarehousesOnOperatorSide, togglingShowOnClientSides, togglingShowOnOperatorSides])

    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center',}}>
                <Typography variant="body2" sx={{marginBottom: 2}}>
                    List the partners that you have access to their inventories and public parts, they will be one of your vendors and you
                    can purchase shared products from them.
                </Typography>
                {
                    <Card sx={{
                        padding: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        backgroundColor: 'background.tertiary',
                        width: '100%',
                    }}>
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBlockEnd: 2}}>
                            <Typography variant="body1" fontWeight={600}>
                                Waiting for Approval
                            </Typography>
                            <Button
                                variant={'outlined'}
                                onClick={() => openCreateConnectionDialog()}
                            >
                                Send a new Request
                            </Button>
                        </Box>
                        {
                            loadingRequests &&
                            Array.from({length: 5}, (_, i) => (
                                <Skeleton
                                    key={`loading-${i}`}
                                    variant="rectangular"
                                    width={'100%'}
                                    sx={(theme) => ({minHeight: 120, width: '100%', borderRadius: (theme).vars?.shape.borderRadius})}
                                />
                            ))
                        }
                        {
                            !loadingRequests && !!requests.length &&
                            requests.map((request) => (
                                <Card
                                    key={request.partnerId}
                                    variant={'outlined'}
                                    sx={{padding: 1, display: 'flex', alignItems: 'center', gap: 1}}
                                >
                                    <LazyImage
                                        alt={'profile'}
                                        noImage={<ImagePlaceholder style={{height: 100, width: 100, borderRadius: 8}}/>}
                                        src={bizFileUrls.getFilePreviewUrl(request.sellerInfo?.logo)}
                                        height={100}
                                        style={{height: 100, width: 100, borderRadius: 8}}
                                    />
                                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, marginLeft: 2, flexGrow: 1,}}>
                                        <Typography variant={'body2'} fontWeight={600}>
                                            {request.sellerInfo?.name ?? ""}
                                        </Typography>
                                        <Typography variant={'caption'}>
                                            {request.sellerInfo?.description ?? ""}
                                        </Typography>
                                        <Typography variant={'caption'} color={'text.secondary'}>
                                            {DateUtils.serverToLocal(request.requestedDateTime).format(DateUtils.dateTimeFormat)}
                                        </Typography>
                                    </Box>
                                    {/*<Box sx={{*/}
                                    {/*    display: 'flex',*/}
                                    {/*    flexDirection: 'column',*/}
                                    {/*    alignItems: 'center',*/}
                                    {/*    justifyContent: 'center',*/}
                                    {/*    width: 80,*/}
                                    {/*}}>*/}
                                    {/*    <SaveOrRollback*/}
                                    {/*        show={{save: false, cancel: fl}}*/}
                                    {/*        showText*/}
                                    {/*        column={false}*/}
                                    {/*        onRollback={(setLoading) => cancelRequest(request, setLoading)}*/}
                                    {/*    />*/}
                                    {/*</Box>*/}
                                </Card>
                            ))
                        }
                        {
                            !loadingRequests && !requests.length &&
                            <Typography variant={'body2'} color={'text.secondary'}>
                                Currently, there are no requests waiting for approval.
                            </Typography>
                        }
                    </Card>
                }
                <Typography variant="body1" fontWeight={600} sx={{marginBlockStart: 4, width: '100%', textAlign: 'start'}}>
                    Current Sellers
                </Typography>
                <PartnerDataGrid
                    {...dataGridSaveConfig}
                    classNames={{
                        toolbar: "pt-3"
                    }}
                    initialLayoutState={{
                        columns: columns,
                    }}
                    columns={dataGridColumns}
                    rows={rows}
                    hideFooterActions
                    hideExporting
                    state={{
                        loading: {state: loadingPartners},
                        density: dataGridDensity,
                        pagination: {pageSize: partners.length, currentPage: 1,},
                    }}
                />
            </Box>
            <CreateConnectionDialog
                open={createConnectionDialogState.open}
                onClose={createConnectionDialogState.onClose}
                onSuccess={createConnectionDialogState.onConnectionCreated}
            />
            <UpdateConnectionEntityDialogState
                open={upsertConnectionDialogState.open}
                onClose={upsertConnectionDialogState.onClose}
                onConnectionUpdated={upsertConnectionDialogState.onConnectionUpdated}
                vendor={upsertConnectionDialogState.vendor}
                connectionId={upsertConnectionDialogState.partnerId}
            />
        </>
    );
}


const initialAddVendorDialogState = {
    open: false,
    setOpen: () => void 0,
    initialValues: {},
    addVendor: () => void 0,
}


const CreateConnectionDialog = ({open, onClose, onSuccess}) => {
    const [submitting, setSubmitting] = useState(false);
    const [vendor, setVendor] = useState();
    const [partnerKey, setPartnerKey] = useState('');
    const [addVendorDialogState, setAddVendorDialogState] = useState(initialAddVendorDialogState);
    const isMounted = useIsMounted();

    /**
     * As soon as the dialog opens:
     * - resets the state values
     */
    useEffect(() => {
        if (open) {
            setSubmitting(false);
            setVendor(null);
            setPartnerKey('')
        }
    }, [open]);

    /**
     * Sends a request to the vendor to connect as a seller.
     * @type {(function(*): Promise<void>)}
     */
    const partnerConnectionSellerRequest = useCallback(async (vendorId) => {
        setSubmitting(true);
        const forApi = {
            vendorId: vendorId,
            partnerPublicKey: partnerKey,
        }
        const response = await Api.partnerConnectionSendSellerRequest(forApi);
        if (!isMounted())
            return;
        setSubmitting(false);
        if (response?.resultFlag) {
            onSuccess(response.data);
            onClose();
        }
    }, [isMounted, onClose, onSuccess, partnerKey])


    /**
     * Creates a new vendor profile online and replaces the current vendor with the new one.
     * @type {(function(*, *): Promise<void>)}
     */
    const createVendorOnlineProfile = useCallback(async (vendor, onResponse) => {
        const forApi = {
            name: vendor.name,
            code: vendor.code,
            email: vendor.email,
            phone: vendor.phone,
        }
        const addResponse = await Api.createVendorOnlineProfile(forApi);
        if (!isMounted())
            return;
        onResponse?.(addResponse);
        if (addResponse?.resultFlag) {
            const replacement = addResponse.data;
            setVendor(replacement);
            void partnerConnectionSellerRequest(replacement.id);
        }
    }, [isMounted, partnerConnectionSellerRequest])

    /**
     * Accepts and connects the vendor as the buyer of the current partner.
     * @type {(function(): Promise<void>)}
     */
    const sendRequest = useCallback(async () => {
        setSubmitting(true);
        const vendorId = (await Api.getVendorInformation(vendor.code))?.data?.id;
        if (!isMounted())
            return;
        if (vendorId == null) {
            setSubmitting(false);
            return setAddVendorDialogState({
                open: true,
                setOpen: () => setAddVendorDialogState(initialAddVendorDialogState),
                addVendor: createVendorOnlineProfile,
                initialValues: {
                    name: vendor?.name,
                    code: vendor?.code,
                    email: vendor?.email,
                    phone: vendor?.phone,
                },
            })
        }
        void partnerConnectionSellerRequest(vendorId);
    }, [vendor?.code, vendor?.name, vendor?.email, vendor?.phone, isMounted, createVendorOnlineProfile, partnerConnectionSellerRequest])


    return (
        <>
            <Dialog open={open} setOpen={onClose}>
                <DialogTitle fontWeight={600}>
                    Send Request
                </DialogTitle>
                <DialogContent className={'d-flex flex-column py-3'}>
                    <DialogContentText variant={'body2'} sx={{marginBlockEnd: 2}}>
                        <span className={'font-weight-semi-bold mr-2'}>Assign Vendor</span>
                        Each seller partner is also a vendor in your system and you will purchase through the assigned vendor.
                    </DialogContentText>
                    <TextField
                        label={'Partner Key'}
                        fullWidth
                        value={partnerKey}
                        onChange={(e) => setPartnerKey(e.target.value)}
                        disabled={submitting}
                        sx={{marginBlockEnd: 2}}
                    />
                    <VendorAutocomplete
                        label={'Vendor'}
                        fullWidth
                        value={vendor}
                        onChange={(e, v) => setVendor(v)}
                        disabled={submitting}
                        filterOptions={options => options.filter(option => option.code !== vendor?.code)}
                    />
                </DialogContent>
                <DialogActions sx={{display: 'flex', justifyContent: 'space-between', padding: 0}}>
                    <Button
                        disabled={submitting}
                        variant={'dialog'}
                        color={'primary'}
                        type={'button'}
                        onClick={() => onClose()}
                        borderRadius={'0 10px 0 10px '}
                    >
                        Close
                    </Button>
                    <Button
                        disabled={submitting || !vendor || !partnerKey?.trim()}
                        onClick={sendRequest}
                        variant={'dialog'}
                        color={'primary'}
                        borderRadius={'8px 0 8px 0'}
                    >
                        {
                            submitting
                                ? "Connecting..."
                                : "Send Request"
                        }
                    </Button>
                </DialogActions>
            </Dialog>
            <AddVendorModal
                addVendor={addVendorDialogState.addVendor}
                initialValues={addVendorDialogState.initialValues}
                open={addVendorDialogState.open}
                setOpen={addVendorDialogState.setOpen}
            />
        </>
    );
}

const UpdateConnectionEntityDialogState = ({open, onClose, vendor, connectionId, onConnectionUpdated}) => {
    const [replacement, setReplacement] = useState();
    const [submitting, setSubmitting] = useState(false);
    const [addVendorDialogState, setAddVendorDialogState] = useState(initialAddVendorDialogState);
    const isMounted = useIsMounted()

    /**
     * As soon as the dialog opens:
     * - resets the state values
     */
    useEffect(() => {
        if (open) {
            setSubmitting(false);
            setReplacement(null);
        }
    }, [open]);

    /**
     * Replaces the customer of the provided connection with the replacement entity.
     * @type {(function(*): Promise<void>)}
     */
    const partnerConnectionReplaceVendor = useCallback(async (replacement) => {
        const forApi = {
            vendorId: replacement.id,
            partnerConnectionId: connectionId,
        }
        setSubmitting(true);
        const response = await Api.partnerConnectionReplaceVendor(forApi);
        if (!isMounted())
            return;
        setSubmitting(false);
        if (response?.resultFlag) {
            onConnectionUpdated(replacement);
            onClose();
        }
    }, [connectionId, isMounted, onClose, onConnectionUpdated])

    /**
     * Creates a new vendor profile online and replaces the current vendor with the new one.
     * @type {(function(*, *): Promise<void>)}
     */
    const createVendorOnlineProfile = useCallback(async (vendor, onResponse) => {
        const forApi = {
            name: vendor.name,
            code: vendor.code,
            email: vendor.email,
            phone: vendor.phone,
        }
        const addResponse = await Api.createVendorOnlineProfile(forApi);
        if (!isMounted())
            return;
        onResponse?.(addResponse);
        if (addResponse?.resultFlag) {
            const replacement = addResponse.data;
            setReplacement(replacement);
            void partnerConnectionReplaceVendor(replacement);
        }
    }, [isMounted, partnerConnectionReplaceVendor])

    /**
     * Replaces the customer of the provided connection with the replacement entity.
     * @type {(function(): Promise<void>)}
     */
    const replaceVendor = useCallback(async () => {
        setSubmitting(true);
        const vendorId = (await Api.getVendorInformation(replacement.code))?.data?.id;
        if (!isMounted())
            return;
        if (vendorId == null) {
            setSubmitting(false);
            return setAddVendorDialogState({
                open: true,
                setOpen: () => setAddVendorDialogState(initialAddVendorDialogState),
                addVendor: createVendorOnlineProfile,
                initialValues: {
                    name: replacement.name,
                    code: replacement.code,
                    email: replacement.email,
                    phone: replacement.phone,
                },
            })
        }
        void partnerConnectionReplaceVendor(replacement);
    }, [createVendorOnlineProfile, isMounted, replacement, partnerConnectionReplaceVendor])

    return (
        <>
            <Dialog open={open} setOpen={onClose}>
                <DialogTitle fontWeight={600}>
                    Change Assigned Vendor
                </DialogTitle>
                <DialogContent className={'d-flex flex-column py-3'}>
                    <DialogContentText variant={'body2'} sx={{marginBlockEnd: 2}}>
                        By changing the related vendor, the partner will use the newly assigned vendor but the old one remains in your
                        system and you can assign the partner to the old vendor again whenever you want.
                    </DialogContentText>
                    <div className={'d-flex align-items-center mb-3'} style={{gap: 12}}>
                        <p className={'text-sm'}>
                            Current Vendor:
                        </p>
                        <p className={'text-sm font-weight-semi-bold tertiary-main'}>
                            {vendor?.name ?? ""}
                        </p>
                    </div>
                    <VendorAutocomplete
                        label={'Replacement Vendor'}
                        fullWidth
                        value={replacement}
                        onChange={(e, v) => setReplacement(v)}
                        disabled={submitting}
                        filterOptions={options => options.filter(option => option.name !== vendor?.name)}
                    />
                </DialogContent>
                <DialogActions sx={{display: 'flex', justifyContent: 'space-between', padding: 0}}>
                    <Button
                        disabled={submitting}
                        variant={'dialog'}
                        color={'primary'}
                        type={'button'}
                        onClick={() => onClose()}
                        borderRadius={'0 10px 0 10px '}
                    >
                        Close
                    </Button>
                    <Button
                        disabled={submitting || !vendor}
                        onClick={replaceVendor}
                        variant={'dialog'}
                        color={'primary'}
                        borderRadius={'8px 0 8px 0'}
                    >
                        {
                            submitting
                                ? "Applying..."
                                : "Change Vendor"
                        }
                    </Button>
                </DialogActions>
            </Dialog>
            <AddVendorModal
                addVendor={addVendorDialogState.addVendor}
                initialValues={addVendorDialogState.initialValues}
                open={addVendorDialogState.open}
                setOpen={addVendorDialogState.setOpen}
            />
        </>
    );
}

export default PartnershipViewSellers;
